import React, { useRef, useState, useEffect } from "react";
import { AppContext } from "./AppContext.jsx";
import * as R from "ramda";
import useSearchValues from "../../common/routing/useSearchValues.js";
import useLocationRestoration from "../../common/routing/useLocationRestoration.js";
import useHashSlash from "../../common/routing/useHashSlash.js";
import useDefaultPathname from "../../common/routing/useDefaultPathname.js";
import { FloorPlanIcon, ListIcon, MapPinIcon } from "../../common/icons.jsx";
import useFetchJson from "../../common/useFetchJson.js";

import {
  NavLink,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";

import ActionBar from "../ActionBar/ActionBar.jsx";
import Filters from "../ActionBar/Filters.jsx";
import FloorPlanListContainer from "../FloorPlanListView/FloorPlanListContainer.jsx";
import UnitListContainer from "../UnitListView/UnitListContainer.jsx";
import MapView from "../MapView/MapView.jsx";

import { any, arrayOf, shape, string } from "prop-types";
import RouteSelect from "../RouteSelect/RouteSelect.jsx";

const optionShape = shape({
  id: any.isRequired, // could be string or number
  label: string.isRequired,
});

const propTypes = {
  filterOptions: shape({
    bedrooms: arrayOf(optionShape),
    bathrooms: arrayOf(optionShape),
    floors: arrayOf(optionShape),
    attributes: arrayOf(optionShape),
    price: arrayOf(optionShape),
  }),
  floorPlanListingTitle: string,
  unitListingTitle: string,
};

const lookupValueById = (id, values) => {
  return R.compose(
    // null is important so the <Select> knows this value is controlled
    R.defaultTo(null),
    R.find(R.propEq("id", id)),
    R.defaultTo([]),
  )(values);
};

const FloorPlansPage = props => {
  const { filterOptions, floorPlanListingTitle, unitListingTitle } = props;

  const scrollToRef = useRef();

  const history = useHistory();
  const location = useLocation();
  // remember and restore the router selections
  useLocationRestoration();
  useHashSlash();

  const url = "/api/v1/floor-plans";
  const dataResponse = useFetchJson(url, { method: "GET" });
  const { sightmapEmbedId = null } = dataResponse.data || {};

  //create context to be used in cards
  const [appContext, setAppContext] = useState({
    hasSightmap: !R.isNil(sightmapEmbedId)
  });

  // Track currently active display.
  const useFindPath = () => {
    const location = useLocation();
    const [currentPath, setCurrentPath] = useState(location.pathname.replace(/\//, ""));
    useEffect(() => {
      setCurrentPath(location.pathname.replace(/\//, ""));
    }, [location]);
    return currentPath;
  };

  const views = [
    {
      id: "plan",
      label: "Plan View",
      icon: FloorPlanIcon,
      Component: FloorPlanListContainer,
    },
    {
      id: "unit",
      label: "Homes View",
      icon: ListIcon,
      Component: UnitListContainer,
    },
  ];

  if (sightmapEmbedId) {
    views.push({
      id: "map",
      label: "Map View",
      icon: MapPinIcon,
      Component: MapView,
    });
  }

  // NOTE: uncommenting this to default to first view (see LMCS-93)
  // if there is no view selected, select the first one
  useDefaultPathname(`/${views[0].id}`);

  // NOTE: removed due to conflicting functionality (see LMCS-93)
  // this is to re-append search to the url after it gets stripped
  // useEffect(() => {
  //   const appendSearch = () => {
  //     const searchData = sessionStorage.getItem("searchString")

  //     if (searchData) {
  //       const removeLeadingQuestionMark = searchData.replace("?", "");
  //       history.replace({
  //         ...location,
  //         search: JSON.parse(removeLeadingQuestionMark)
  //       })
  //       sessionStorage.removeItem("searchString")
  //     }
  //   }

  //   appendSearch();
  // }, [history, location])


  const { searchValues, getLocationFor } = useSearchValues();
  // searchValues is what is in the url, resolve those values to the actual objects
  const filterValues = {
    price: lookupValueById(searchValues.price, filterOptions.price),
    bedrooms: lookupValueById(searchValues.bedrooms, filterOptions.bedrooms),
    bathrooms: lookupValueById(searchValues.bathrooms, filterOptions.bathrooms),
    floors: lookupValueById(searchValues.floors, filterOptions.floors),
    attributes: R.map(id => lookupValueById(id, filterOptions.attributes))(
      searchValues.attributes,
    ),
    available_on: searchValues.available_on,
  };

  // get the new url that includes this new key/value pair in the querystring
  const handleFilterChange = (key, value) => {
    const newUrl = getLocationFor({ [key]: value, page: 1 });
    history.push(newUrl);
  };

  const filterOpts = filters => {
    if (location.pathname.includes("/map")) {
      return R.omit(["floors"], filters);
    }
    return filters;
  };

  return (
    <AppContext.Provider value={[appContext, setAppContext]}>
      <div className="floor-plans" ref={scrollToRef}>
        <ActionBar
          activeView={useFindPath()}
          left={
            <Filters
              options={filterOpts(filterOptions)}
              onChange={handleFilterChange}
              values={filterValues}
            />
          }
          right={
            <RouteSelect
              placeholder="View"
              selectProps={{
                items: views,
                itemToString: R.prop("label"),
              }}
            />
            // <div className="action-bar__toggles">
            //   {views.map(view => (
            //     <div key={view.id} className="action-bar__button">
            //       <NavLink
            //         to={{ ...location, pathname: `/${view.id}` }}
            //         className="toggle-button"
            //         activeClassName="toggle-button--selected"
            //         onClick={() => setActiveView(view)}
            //       >
            //         <view.icon className="toggle-button__icon" />
            //         {view.label}
            //       </NavLink>
            //     </div>
            //   ))}
            // </div>
          }
        />

        <Switch>
          {views.map(view => {
            const { id, Component } = view;
            return (
              <Route
                key={id}
                path={`/${id}`}
                render={props => (
                  <Component
                    scrollToRef={scrollToRef}
                    floors={filterOptions.floors}
                    floorPlanListingTitle={floorPlanListingTitle}
                    unitListingTitle={unitListingTitle}
                  />
                )}
              />
            );
          })}
        </Switch>
      </div>
    </AppContext.Provider>
  );
};

FloorPlansPage.propTypes = propTypes;
export default FloorPlansPage;
